import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV } from '@/lib/testIds';

const LINKS = [
  { to: '/', label: 'Home', key: 'home' },
  { to: '/about', label: 'About', key: 'about' },
  { to: '/services', label: 'Services', key: 'services' },
  { to: '/case-studies', label: 'Case Studies', key: 'case-studies' },
  { to: '/testimonials', label: 'Testimonials', key: 'testimonials' },
  { to: '/contact', label: 'Contact', key: 'contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-2xl bg-[#040914]/85 border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2.5 group">
          <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 grid place-items-center font-heading font-bold text-[#040914] text-lg shadow-lg shadow-cyan-500/20">
            S
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base font-semibold tracking-tight">SKV Digital Labs</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-ink">AI Growth · Transformation</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === '/'}
              data-testid={NAV.link(l.key)}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive ? 'text-cyan-300' : 'text-white/70 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute inset-x-3 -bottom-1 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid={NAV.cta}
            className="hidden sm:inline-flex btn-shimmer items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-400 text-[#040914] font-semibold text-sm hover:bg-cyan-300 transition-colors"
          >
            Book a Strategy Call
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white hover:bg-white/5"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#040914]/95 backdrop-blur-2xl">
          <div className="px-6 py-6 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === '/'}
                data-testid={`mobile-${NAV.link(l.key)}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium ${
                    isActive ? 'bg-white/5 text-cyan-300' : 'text-white/80 hover:bg-white/5'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-3 px-5 py-3 rounded-full bg-cyan-400 text-[#040914] font-semibold text-sm text-center"
            >
              Book a Strategy Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
